import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Plus, Trash2, Send, Save, Ban, ArrowLeft, AlertCircle } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { Field, Input } from '@/components/Input';
import { Combobox } from '@/components/Combobox';
import { NumericInput } from '@/components/NumericInput';
import { DocstatusPill } from '@/components/StatusPill';
import { api } from '@/lib/api';
import { toast } from '@/components/Toaster';

interface StockEntryLine {
  id: string;
  row_index: number;
  item_id: string;
  qty: string;
  uom: string;
  source_warehouse_id?: string;
  target_warehouse_id?: string;
  basic_rate: string;
  basic_amount: string;
}
interface StockEntry {
  id: string;
  name: string;
  posting_date: string;
  purpose: 'material_receipt' | 'material_issue' | 'material_transfer' | 'manufacture';
  work_order_id?: string;
  total_outgoing_value: string;
  total_incoming_value: string;
  remarks?: string;
  docstatus: 0 | 1 | 2;
  items: StockEntryLine[];
}

interface Item      { id: string; code: string; name: string; stock_uom: string }
interface Warehouse { id: string; code: string; name: string; is_group?: boolean }

const PURPOSES = [
  { value: 'material_receipt',  label: 'Material Receipt',  description: 'Bring stock in (target warehouse only)' },
  { value: 'material_issue',    label: 'Material Issue',    description: 'Take stock out (source warehouse only)' },
  { value: 'material_transfer', label: 'Material Transfer', description: 'Move between warehouses' },
  { value: 'manufacture',       label: 'Manufacture',       description: 'Consume raw materials + receive finished goods' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const rid = () => Math.random().toString(36).slice(2);

interface DraftLine {
  rowId: string;
  item_id: string;
  qty: string;
  uom: string;
  source_warehouse_id: string;
  target_warehouse_id: string;
  basic_rate: string;
}

export function StockEntryForm() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isNew = !id || id === 'new';

  const { data: existing } = useQuery({
    queryKey: ['stock-entry', id],
    queryFn:  () => api<StockEntry>(`/stock/stock-entries/${id}`),
    enabled:  !isNew,
  });
  const { data: itemsResp } = useQuery({
    queryKey: ['items'],
    queryFn:  () => api<{ items: Item[] }>('/accounting/items'),
  });
  const { data: warehousesResp } = useQuery({
    queryKey: ['warehouses'],
    queryFn:  () => api<{ items: Warehouse[] }>('/stock/warehouses'),
  });
  const items      = itemsResp?.items ?? [];
  const warehouses = useMemo(
    () => (warehousesResp?.items ?? []).filter((w) => !w.is_group),
    [warehousesResp],
  );

  const [postingDate, setPostingDate] = useState(todayISO());
  const [purpose, setPurpose]         = useState<StockEntry['purpose']>('material_receipt');
  const [remarks, setRemarks]         = useState('');
  const [lines, setLines]             = useState<DraftLine[]>([
    { rowId: rid(), item_id: '', qty: '1', uom: '', source_warehouse_id: '', target_warehouse_id: '', basic_rate: '0' },
  ]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!existing) return;
    setPostingDate(existing.posting_date.slice(0, 10));
    setPurpose(existing.purpose);
    setRemarks(existing.remarks ?? '');
    setLines(existing.items.map((it) => ({
      rowId: rid(),
      item_id: it.item_id,
      qty: it.qty,
      uom: it.uom,
      source_warehouse_id: it.source_warehouse_id ?? '',
      target_warehouse_id: it.target_warehouse_id ?? '',
      basic_rate: it.basic_rate,
    })));
  }, [existing]);

  const editable  = isNew || existing?.docstatus === 0;
  const submitted = existing?.docstatus === 1;
  const cancelled = existing?.docstatus === 2;
  const needsSource = purpose === 'material_issue' || purpose === 'material_transfer' || purpose === 'manufacture';
  const needsTarget = purpose === 'material_receipt' || purpose === 'material_transfer' || purpose === 'manufacture';

  function validate(): string[] {
    const issues: string[] = [];
    if (!postingDate) issues.push('Posting date is required.');
    if (lines.length === 0) issues.push('Add at least one line item.');
    lines.forEach((l, i) => {
      const n = i + 1;
      if (!l.item_id) issues.push(`Line ${n}: item required.`);
      const qty = Number(l.qty);
      if (!Number.isFinite(qty) || qty <= 0) issues.push(`Line ${n}: qty must be > 0.`);
      if (needsSource && !l.source_warehouse_id) issues.push(`Line ${n}: source warehouse required.`);
      if (needsTarget && !l.target_warehouse_id) issues.push(`Line ${n}: target warehouse required.`);
    });
    return issues;
  }

  const createMutation = useMutation({
    mutationFn: () => api<StockEntry>('/stock/stock-entries', {
      method: 'POST',
      body: {
        posting_date: postingDate,
        purpose,
        remarks: remarks || undefined,
        items: lines.map((l) => ({
          item_id: l.item_id,
          qty: l.qty,
          uom: l.uom || undefined,
          source_warehouse_id: needsSource ? l.source_warehouse_id : undefined,
          target_warehouse_id: needsTarget ? l.target_warehouse_id : undefined,
          basic_rate: l.basic_rate || undefined,
        })),
      },
    }),
    onSuccess: (se) => {
      toast.success(`Saved ${se.name}`);
      void qc.invalidateQueries({ queryKey: ['list', 'stock-entries'] });
      void navigate({ to: `/stock/stock-entries/${se.id}` as never });
    },
    onError: (e: Error) => { setErr(e.message); toast.error('Could not save draft', e.message); },
  });

  const updateMutation = useMutation({
    mutationFn: () => api<StockEntry>(`/stock/stock-entries/${id}`, {
      method: 'PUT',
      body: {
        posting_date: postingDate,
        remarks: remarks || undefined,
        items: lines.map((l) => ({
          item_id: l.item_id,
          qty: l.qty,
          uom: l.uom || undefined,
          source_warehouse_id: needsSource ? l.source_warehouse_id : undefined,
          target_warehouse_id: needsTarget ? l.target_warehouse_id : undefined,
          basic_rate: l.basic_rate || undefined,
        })),
      },
    }),
    onSuccess: () => {
      toast.success('Saved', 'Draft updated.');
      void qc.invalidateQueries({ queryKey: ['stock-entry', id] });
      void qc.invalidateQueries({ queryKey: ['doctype', '/stock/stock-entries'] });
    },
    onError: (e: Error) => { setErr(e.message); toast.error('Save failed', e.message); },
  });

  const lifecycle = (path: string, successMsg: string) => useMutation({
    mutationFn: () => api<StockEntry>(`/stock/stock-entries/${id}/${path}`, { method: 'POST' }),
    onSuccess: () => { toast.success(successMsg); void qc.invalidateQueries({ queryKey: ['stock-entry', id] }); },
    onError:   (e: Error) => { setErr(e.message); toast.error(`Could not ${path}`, e.message); },
  });
  const submitMutation = lifecycle('submit', 'Submitted — stock posted');
  const cancelMutation = lifecycle('cancel', 'Cancelled — entries reversed');

  function onSaveDraft() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) {
      setErr(issues.join(' '));
      toast.error('Fix these first', issues[0]);
      return;
    }
    createMutation.mutate();
  }

  function onSave() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) {
      setErr(issues.join(' '));
      toast.error('Fix these first', issues[0]);
      return;
    }
    updateMutation.mutate();
  }

  const title = isNew ? <>New Stock Entry</> : <span>{existing?.name ?? '…'}</span>;

  return (
    <>
      <PageHeader
        crumbs={[
          { label: 'Stock', to: '/stock' },
          { label: 'Stock Entries', to: '/stock/stock-entries' },
          { label: isNew ? 'New' : (existing?.name ?? '…') },
        ]}
        title={title}
        status={existing && <DocstatusPill docstatus={existing.docstatus} />}
        actions={
          <>
            <Button variant="ghost" asChild>
              <Link to={'/stock/stock-entries' as never}>
                <ArrowLeft className="size-4" /> Back
              </Link>
            </Button>
            {editable && isNew && (
              <Button onClick={onSaveDraft} loading={createMutation.isPending}>
                <Save className="size-4" /> Save draft
              </Button>
            )}
            {editable && !isNew && (
              <Button variant="secondary" onClick={onSave} loading={updateMutation.isPending}>
                <Save className="size-4" /> Save
              </Button>
            )}
            {editable && !isNew && (
              <Button onClick={() => submitMutation.mutate()} loading={submitMutation.isPending}>
                <Send className="size-4" /> Submit
              </Button>
            )}
            {submitted && (
              <Button variant="danger" onClick={() => cancelMutation.mutate()} loading={cancelMutation.isPending}>
                <Ban className="size-4" /> Cancel
              </Button>
            )}
          </>
        }
      />

      <div className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8 space-y-4 max-w-[1100px]">
        {err && (
          <Card className="!p-3 bg-brand-error/5 border-brand-error/30 flex items-start gap-2">
            <AlertCircle className="size-4 text-brand-error mt-0.5 shrink-0" />
            <div className="text-body-sm text-brand-error">{err}</div>
          </Card>
        )}
        {cancelled && (
          <Card className="!p-3 bg-brand-error/5 border-brand-error/30 text-body-sm text-brand-error">
            This entry was cancelled. Reversing stock ledger entries were posted automatically.
          </Card>
        )}

        <Card>
          <CardTitle>Entry</CardTitle>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Field label="Purpose">
              <Combobox
                value={purpose}
                options={PURPOSES}
                onChange={(v) => setPurpose((v as StockEntry['purpose']) ?? 'material_receipt')}
                disabled={!editable}
              />
            </Field>
            <Field label="Posting date">
              <Input type="date" value={postingDate} onChange={(e) => setPostingDate(e.target.value)} disabled={!editable} />
            </Field>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-3">
            <CardTitle>Items</CardTitle>
            {editable && (
              <Button size="sm" variant="secondary" onClick={() =>
                setLines((prev) => [...prev, { rowId: rid(), item_id: '', qty: '1', uom: '', source_warehouse_id: '', target_warehouse_id: '', basic_rate: '0' }])}>
                <Plus className="size-3.5" /> Add line
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-body-sm">
              <thead className="bg-surface-soft border-b border-hairline text-micro-uppercase text-stone">
                <tr>
                  <th className="text-left font-medium px-2 py-2">Item</th>
                  {needsSource && <th className="text-left font-medium px-2 py-2">From WH</th>}
                  {needsTarget && <th className="text-left font-medium px-2 py-2">To WH</th>}
                  <th className="text-right font-medium px-2 py-2 w-24">Qty</th>
                  <th className="text-right font-medium px-2 py-2 w-32">Basic rate</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.rowId} className="border-t border-hairline">
                    <td className="px-2 py-1">
                      <Combobox
                        value={l.item_id || null}
                        options={items.map((it) => ({ value: it.id, label: it.name, description: it.code }))}
                        onChange={(v) => {
                          const picked = items.find((it) => it.id === v);
                          setLines((prev) => prev.map((p, idx) => idx === i ? {
                            ...p, item_id: v ?? '', uom: picked?.stock_uom ?? p.uom,
                          } : p));
                        }}
                        disabled={!editable}
                      />
                    </td>
                    {needsSource && (
                      <td className="px-2 py-1">
                        <Combobox
                          value={l.source_warehouse_id || null}
                          options={warehouses.map((w) => ({ value: w.id, label: w.code, description: w.name }))}
                          onChange={(v) => setLines((prev) => prev.map((p, idx) => idx === i ? { ...p, source_warehouse_id: v ?? '' } : p))}
                          disabled={!editable}
                        />
                      </td>
                    )}
                    {needsTarget && (
                      <td className="px-2 py-1">
                        <Combobox
                          value={l.target_warehouse_id || null}
                          options={warehouses.map((w) => ({ value: w.id, label: w.code, description: w.name }))}
                          onChange={(v) => setLines((prev) => prev.map((p, idx) => idx === i ? { ...p, target_warehouse_id: v ?? '' } : p))}
                          disabled={!editable}
                        />
                      </td>
                    )}
                    <td className="px-2 py-1">
                      <NumericInput value={l.qty} disabled={!editable}
                        onChange={(v) => setLines((prev) => prev.map((p, idx) => idx === i ? { ...p, qty: v } : p))} />
                    </td>
                    <td className="px-2 py-1">
                      <NumericInput value={l.basic_rate} disabled={!editable}
                        onChange={(v) => setLines((prev) => prev.map((p, idx) => idx === i ? { ...p, basic_rate: v } : p))} />
                    </td>
                    <td className="px-2 py-1">
                      {editable && lines.length > 1 && (
                        <button type="button" onClick={() => setLines((prev) => prev.filter((_, idx) => idx !== i))}
                          className="text-stone hover:text-brand-error" aria-label="Remove">
                          <Trash2 className="size-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card>
          <Field label="Remarks">
            <textarea className="input-base !h-auto !py-2" rows={3}
              value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!editable} />
          </Field>
        </Card>
      </div>
    </>
  );
}
